// ###### IMPORTS
import React, { useState, useEffect, useRef } from 'react';
import {
  PlusIcon,
  XMarkIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  CameraIcon,
} from '@heroicons/react/24/solid';
import ModalOperationAdd from './ModalOperationAdd';
import ModalAssetAdd from './ModalAssetAdd';
import ModalReturnAdd from './ModalReturnAdd';
import ModalSnapshotAdd from './ModalSnapshotAdd';

// ###### CONFIG
const MENU_ITEMS = [
  {
    key: 'asset',
    label: 'Asset',
    icon: BanknotesIcon,
    enabled: true,
  },
  {
    key: 'operation',
    label: 'Operation',
    icon: ArrowsRightLeftIcon,
    enabled: true,
  },
  {
    key: 'return',
    label: 'Return',
    icon: ReceiptPercentIcon,
    enabled: true,
  },
  {
    key: 'snapshot',
    label: 'Snapshot',
    icon: CameraIcon,
    enabled: true,
  },
];

// ###### MAIN COMPONENT
const FabAssetsMenu = ({ assets = [], onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [isOperationModalOpen, setIsOperationModalOpen] = useState(false);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleItemClick = (key) => {
    setOpen(false);
    if (key === 'operation') setIsOperationModalOpen(true);
    if (key === 'asset') setIsAssetModalOpen(true);
    if (key === 'return') setIsReturnModalOpen(true);
    if (key === 'snapshot') setIsSnapshotModalOpen(true);
  };

  return (
    <>
      <div ref={containerRef} className="fixed bottom-20 right-6 flex flex-col items-end gap-2 z-40">
        {open && (
          <div className="flex flex-col items-end gap-1.5 mb-1">
            {MENU_ITEMS.map(({ key, label, icon: Icon, enabled }) => (
              <button
                key={key}
                type="button"
                disabled={!enabled}
                onClick={() => enabled && handleItemClick(key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full shadow text-sm font-medium transition-all
                  ${
                    enabled
                      ? 'bg-white text-green-800 hover:bg-green-50 cursor-pointer'
                      : 'bg-white text-gray-400 border border-gray-200 cursor-not-allowed opacity-60'
                  }`}
              >
                <span>{label}</span>
                <Icon className="h-4 w-4 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="bg-green-800 hover:bg-green-900 text-white p-3 rounded-full shadow-lg transition-transform duration-200"
          style={{ transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
          aria-label={open ? 'Fechar menu' : 'Adicionar'}
        >
          {open ? <XMarkIcon className="h-8 w-8" /> : <PlusIcon className="h-8 w-8" />}
        </button>
      </div>

      <ModalOperationAdd
        isOpen={isOperationModalOpen}
        onClose={() => setIsOperationModalOpen(false)}
        onSuccess={() => {
          setIsOperationModalOpen(false);
          onSuccess?.();
        }}
        assets={assets}
      />

      <ModalAssetAdd
        isOpen={isAssetModalOpen}
        onClose={() => setIsAssetModalOpen(false)}
        onSuccess={() => {
          setIsAssetModalOpen(false);
          onSuccess?.();
        }}
        assets={assets}
      />

      <ModalReturnAdd
        isOpen={isReturnModalOpen}
        onClose={() => setIsReturnModalOpen(false)}
        onSuccess={() => {
          setIsReturnModalOpen(false);
          onSuccess?.();
        }}
        assets={assets}
      />

      <ModalSnapshotAdd
        isOpen={isSnapshotModalOpen}
        onClose={() => setIsSnapshotModalOpen(false)}
        onSuccess={() => {
          setIsSnapshotModalOpen(false);
          onSuccess?.();
        }}
        assets={assets}
      />
    </>
  );
};

export default FabAssetsMenu;
